import constants from '@Common/Core/Constant';
import { TranslationTableKind } from '@Common/Enums/Enum';
import instanceFactory, {
    AbstractInstanceItem,
    AutoMetricReference,
    Categorized,
    DocumentDescriptor,
    InsightObject,
    InsightObjectReference,
    TranslationTable,
    Typed
} from '@InstanceFactory';
import formatHelper from '@Common/Helpers/FormatHelper';

import categoryMap from '../Core/CategoryMap';
import { documentCategories } from '@Common/Core/DocumentCategories';

export type AllInstancesName = KeyOfByType<typeof instanceFactory, AbstractInstanceItem<any>>;

namespace documentUtils {
    export function getReferencedClassName(documentReference: Typed | string): AllInstancesName | undefined {
        const docType = _.isString(documentReference) ? documentReference : documentReference?.$type ?? '';
        const name = docType.split(',').shift()!.split('.').pop();
        return ((name !== docType ? name : undefined) as unknown) as AllInstancesName;
    }

    export function getDocumentCategory(document: Typed): string | undefined {
        return getDocumentCategories(document)?.category;
    }

    export function getDocumentCategories(document: Typed): Categorized | undefined {
        const className = documentUtils.getReferencedClassName(document);
        if (AutoMetricReference.instanceName === className) {
            return { category: documentCategories.autoMetric, groupCategory: documentCategories.metric };
        }
        const definition = className && (instanceFactory[className] as Categorized);
        return definition;
    }

    export function createDocumentReference(
        document: { $type?: string; id: string; name: string; category?: string },
        typeName?: string
    ): { $type: string; id: string; name: string; document: any; isChanged: boolean } {
        let documentInstance = null;
        if (!typeName) {
            if (document.category) {
                const type = categoryMap[document.category];
                typeName = instanceFactory[type].typeName;
            } else if (document.$type && document.$type !== constants.documentDescriptorType) {
                typeName = document.$type;
                documentInstance = document;
            } else {
                throw new Error('Need to be specified typeName, document.category, documentType');
            }
        }

        return {
            $type: getDocumentReferenceTypeName(typeName),
            id: document.id,
            name: document.name,
            document: documentInstance,
            isChanged: false
        };
    }

    export function getDocumentReferenceTypeName(typeName: string): string;
    export function getDocumentReferenceTypeName(document: AbstractInstanceItem<any>): string;
    export function getDocumentReferenceTypeName(typeNameOrDocument: string | AbstractInstanceItem<any>): string {
        let typeName = typeNameOrDocument;
        if (!_.isString(typeNameOrDocument)) {
            typeName = typeNameOrDocument.typeName;
        }
        return 'AltoSoft.Insight.Common.RepositoryInterfaces.DocumentReference`1[[{0}]], AltoSoft.Insight.Common'.format(typeName);
    }

    export function createInsightReference<T extends InsightObject>(
        insightObject: T,
        typeName?: string,
        setDocument?: false
    ): InsightObjectReference<T> {
        if (!typeName) {
            if (!insightObject.$type) {
                throw new Error('Need to be specified typeName or document.category');
            }
            typeName = insightObject.$type;
        }
        const referenceType = 'AltoSoft.Insight.DataDefs.DocumentModels.InsightObjectReference`1[[{0}]], AltoSoft.Insight.DataDefs'.format(typeName);
        return {
            $type: referenceType,
            id: insightObject.id,
            name: insightObject.name,
            document: setDocument === false ? null : insightObject,
            isChanged: false
        };
    }

    export function createDocumentDescriptorFromReference(
        documentReference: { $type?: string; id?: string; name?: string; document?: any },
        category?: string
    ): DocumentDescriptor {
        if (documentReference.$type) {
            const className = documentUtils.getReferencedClassName(documentReference);
            const classInfo = instanceFactory[className] as Categorized;
            if (!classInfo) {
                throw new Error(`Can not create DocumentDescriptor. Referenced class '${className}' is not found in InstanceFactory.`);
            }
            if (!classInfo.category) {
                throw new Error(`Can not create DocumentDescriptor. Referenced class '${className}' doesnt contain category in InstanceFactory.`);
            }

            category = classInfo.category;
        } else if (!category) {
            throw new Error('Can not create DocumentDescriptor. Reference has no $type and category is not specified.');
        }
        const attributes = [];
        const document = documentReference.document;
        if (document) {
            if (TranslationTable.isInstanceOfType(document)) {
                document.tableKind === TranslationTableKind.static;
                attributes.push({ name: 'TranslationTableType', value: 'Static' });
            }
        }

        return DocumentDescriptor.create({
            attributes,
            $type: constants.documentDescriptorType,
            id: documentReference.id,
            name: documentReference.name,
            isNew: false,
            category: category
        });
    }

    export function getAttrValue(documentDescriptor: DocumentDescriptor | { attributes: _.Dictionary<string> }, attrName: string): string | undefined;
    export function getAttrValue(
        documentDescriptor: DocumentDescriptor | { attributes: _.Dictionary<string> },
        attrName: string,
        defaultValue: string
    ): string;
    export function getAttrValue(
        documentDescriptor: DocumentDescriptor | { attributes: _.Dictionary<string> },
        attrName: string,
        defaultValue?: string
    ): string | undefined {
        if (documentDescriptor) {
            if (_.isArray(documentDescriptor.attributes)) {
                const attributes = documentDescriptor.attributes;
                const attr = attributes.find(attr => attr.name === attrName);
                if (attr) {
                    return attr.value;
                }
            } else if (_.isPlainObject(documentDescriptor.attributes)) {
                return attrName in documentDescriptor.attributes ? documentDescriptor.attributes[attrName] : defaultValue;
            }
        }
        return defaultValue;
    }

    export function isOnDemandDocument(documentDescriptor: DocumentDescriptor): boolean {
        const attr = _.filter(documentDescriptor.attributes, { name: 'RecordVirtual' });
        return attr.length && attr[0].value.toLowerCase() === 'true';
    }

    export function isAutogeneratedProcess(documentDescriptor: DocumentDescriptor): boolean {
        const attr = this.getAttrValue(documentDescriptor, 'IsAutogenerated');
        return attr && attr.toLowerCase() === 'true';
    }

    export function isRecordOnRecordDocument(documentDescriptor: DocumentDescriptor): boolean {
        const recordType = getAttrValue(documentDescriptor, 'RecordType', 'recordDerived');
        switch (recordType) {
            case 'recordDirect':
                return false;
            case 'recordDerived':
            default:
                return true;
        }
    }

    export function isInvalidDBName(value: string, projectId: any): string {
        if (!value) {
            return strings.invalidDBName.empty;
        }
        const maxDbNameLength = getMaxLengthDbName(projectId);
        if (value.length > maxDbNameLength) {
            return strings.invalidDBName.longerName.format(maxDbNameLength);
        }

        if (/[\.\*\"\'`\[\]\\?]/g.test(value)) {
            return strings.invalidDBName.invalidChar;
        }
        return null;
    }

    export function escapeDBName(value: string): string {
        return value.replace(/[\.\*\"\'`\[\]\\?]/g, '_').trim();
    }

    export function getMaxLengthDbName(projectId: Guid): number {
        if (projectId) {
            return _.find(altosoft.model.user.projects, p => p.id === projectId).maxDbNameLength;
        }
        return 30;
    }

    export function getUniqueDBName(candidate: string, existed: string[], projectId?: any): string {
        let count = 0;
        let result: string;
        candidate = escapeDBName(candidate);
        const existedSet = new Set(_.map(existed, i => i.toLowerCase()));

        const maxDbNameLength = getMaxLengthDbName(projectId);
        if (candidate.length > maxDbNameLength) {
            candidate = candidate.slice(0, maxDbNameLength - 3);
            count = 1;
        }

        do {
            if (count > 0) {
                result = candidate + '_' + formatHelper.formatNumber(count, '00');
            } else {
                result = candidate;
            }
            count++;
        } while (existedSet.has(result.toLowerCase()));

        return result;
    }
}

export default documentUtils;
